<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  template: {
    type: Object,
    default: () => ({}),
  },
  initialMode: {
    type: String,
    default: 'send',
    validator: value => ['send', 'schedule'].includes(value),
  },
});

const emit = defineEmits(['sendMessage', 'scheduleMessage', 'resetTemplate']);

const { t } = useI18n();
const sendMode = ref(props.initialMode);
const scheduledAt = ref('');
const scheduleError = ref('');
const pendingAction = ref('send');

const minimumDateTime = computed(() => {
  const minimumTime = new Date(Date.now() + 60000);
  const localTime = new Date(
    minimumTime.getTime() - minimumTime.getTimezoneOffset() * 60000
  );
  return localTime.toISOString().slice(0, 16);
});

const isScheduleMode = computed(() => sendMode.value === 'schedule');
const isScheduleInvalid = computed(
  () =>
    isScheduleMode.value &&
    (!scheduledAt.value || scheduledAt.value < minimumDateTime.value)
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const handleSendMessage = payload => {
  if (pendingAction.value === 'schedule') {
    emit('scheduleMessage', {
      ...payload,
      scheduledAt: formatToUTCString(scheduledAt.value),
    });
  } else {
    emit('sendMessage', payload);
  }
};

const handleResetTemplate = () => {
  emit('resetTemplate');
};

const handleSendNow = sendMessage => {
  pendingAction.value = 'send';
  scheduleError.value = '';
  sendMessage();
};

const handleSchedule = sendMessage => {
  if (!scheduledAt.value || scheduledAt.value < minimumDateTime.value) {
    scheduleError.value = t('WHATSAPP_TEMPLATES.SCHEDULE.DATE_ERROR');
    return;
  }

  pendingAction.value = 'schedule';
  scheduleError.value = '';
  sendMessage();
};

watch(
  () => props.initialMode,
  value => {
    sendMode.value = value;
  }
);

watch(sendMode, () => {
  scheduleError.value = '';
});
</script>

<template>
  <div class="w-full">
    <WhatsAppTemplateParser
      :template="template"
      @send-message="handleSendMessage"
      @reset-template="handleResetTemplate"
    >
      <template #actions="{ sendMessage, resetTemplate, disabled }">
        <footer class="flex flex-col gap-3">
          <div class="inline-flex p-1 rounded-lg bg-n-alpha-black2 self-end">
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-md"
              :class="
                sendMode === 'send'
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11'
              "
              @click="sendMode = 'send'"
            >
              {{ t('WHATSAPP_TEMPLATES.SCHEDULE.SEND_NOW') }}
            </button>
            <button
              type="button"
              class="px-3 py-1.5 text-sm rounded-md"
              :class="
                sendMode === 'schedule'
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11'
              "
              @click="sendMode = 'schedule'"
            >
              {{ t('WHATSAPP_TEMPLATES.SCHEDULE.SCHEDULE') }}
            </button>
          </div>

          <Input
            v-if="isScheduleMode"
            v-model="scheduledAt"
            type="datetime-local"
            :min="minimumDateTime"
            :label="t('WHATSAPP_TEMPLATES.SCHEDULE.DATE_LABEL')"
            :placeholder="t('WHATSAPP_TEMPLATES.SCHEDULE.DATE_PLACEHOLDER')"
            :message="scheduleError"
            :message-type="scheduleError ? 'error' : 'info'"
          />

          <div class="flex gap-2 justify-end">
            <NextButton
              faded
              slate
              type="reset"
              :label="$t('WHATSAPP_TEMPLATES.PARSER.GO_BACK_LABEL')"
              @click="resetTemplate"
            />
            <NextButton
              v-if="!isScheduleMode"
              type="button"
              :label="$t('WHATSAPP_TEMPLATES.PARSER.SEND_MESSAGE_LABEL')"
              :disabled="disabled"
              @click="handleSendNow(sendMessage)"
            />
            <NextButton
              v-else
              type="button"
              icon="i-lucide-clock"
              :label="t('WHATSAPP_TEMPLATES.SCHEDULE.SCHEDULE_BUTTON')"
              :disabled="disabled || isScheduleInvalid"
              @click="handleSchedule(sendMessage)"
            />
          </div>
        </footer>
      </template>
    </WhatsAppTemplateParser>
  </div>
</template>
